import { CheckCircle2, MonitorUp, Play, Sparkles, TriangleAlert } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { detectHdrCapabilities, UNKNOWN_HDR_CAPABILITIES, type HdrCapabilities } from '../lib/hdr'

interface HdrTestState {
  fixture: string
  canPlay: string
  status: 'loading' | 'ready' | 'playing' | 'error'
  currentTime: number
  duration: number
  width: number
  height: number
  error: string
  capabilities: HdrCapabilities
}

const FIXTURE = './test-media/vela-hdr10-proof.mp4'

export function HdrTestScreen() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [state, setState] = useState<HdrTestState>({
    fixture: FIXTURE,
    canPlay: '',
    status: 'loading',
    currentTime: 0,
    duration: 0,
    width: 0,
    height: 0,
    error: '',
    capabilities: UNKNOWN_HDR_CAPABILITIES,
  })

  useEffect(() => {
    const video = videoRef.current
    const canPlay = video?.canPlayType('video/mp4; codecs="hvc1.2.4.L153.B0"') || ''
    setState((value) => ({ ...value, canPlay }))
    detectHdrCapabilities().then((capabilities) => setState((value) => ({ ...value, capabilities })))
  }, [])

  useEffect(() => {
    window.__VELA_HDR_TEST__ = state
  }, [state])

  function update(status?: HdrTestState['status']) {
    const video = videoRef.current
    if (!video) return
    setState((value) => ({
      ...value,
      status: status || value.status,
      currentTime: video.currentTime || 0,
      duration: video.duration || 0,
      width: video.videoWidth || 0,
      height: video.videoHeight || 0,
    }))
  }

  function start() {
    videoRef.current?.play().then(() => update('playing')).catch((error) => setState((value) => ({ ...value, status: 'error', error: error instanceof Error ? error.message : 'Playback rejected' })))
  }

  return (
    <main className="hdr-test-screen">
      <video
        ref={videoRef}
        src={FIXTURE}
        muted
        loop
        playsInline
        autoPlay
        onLoadedMetadata={() => { update('ready'); start() }}
        onPlaying={() => update('playing')}
        onTimeUpdate={() => update()}
        onError={() => setState((value) => ({ ...value, status: 'error', error: videoRef.current?.error?.message || 'The TV rejected the HDR10 fixture.' }))}
      />
      <div className="hdr-test-shade" />
      <section>
        <span className="hdr-test-icon"><Sparkles size="2.75rem" /></span>
        <small>VELA LIVE-TV PROOF</small>
        <h1>HDR10 output test</h1>
        <p>This packaged 10-bit HEVC fixture carries BT.2020, PQ, mastering-display and MaxCLL metadata. Look for the LG panel&apos;s HDR badge now.</p>
        <div className="hdr-test-results">
          <span className={state.canPlay ? 'ok' : 'warn'}>{state.canPlay ? <CheckCircle2 /> : <TriangleAlert />} HEVC Main 10: {state.canPlay || 'not advertised'}</span>
          <span className={state.capabilities.hdr10 === 'supported' ? 'ok' : 'warn'}><MonitorUp /> Display path: {state.capabilities.display} · HDR10 {state.capabilities.hdr10}</span>
          <span className={state.status === 'playing' ? 'ok' : 'warn'}><Play /> Fixture: {state.status} · {state.width}×{state.height} · {state.currentTime.toFixed(1)} sec</span>
        </div>
        {state.status !== 'playing' ? <button className="primary-button" data-focusable data-autofocus onClick={start}><Play fill="currentColor" /> Start HDR fixture</button> : null}
        <strong className="hdr-manual-check">Required live check: LG HDR badge appears and bars show without washed-out colour.</strong>
      </section>
    </main>
  )
}
